import { useEffect, useRef, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { apiCall } from 'utils/ApiCall';
import { Routes, HttpMethod } from 'types/Enums';
import { SettingsBody, SettingsResponse } from 'types/TypesSettings';

const defaultSettings = {
  allow_messages_from_anyone: true,
  notifications: true,
  filter_offensive: true
};

export default function useSettings() {
  const [settings, setSettings] = useState<SettingsBody>(defaultSettings);
  const [loading, setLoading] = useState<boolean>(true);
  const { enqueueSnackbar } = useSnackbar();

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUpdatesRef = useRef<Partial<SettingsBody>>({});
  const prevSettingsRef = useRef<SettingsBody>(defaultSettings);
  const isMountedRef = useRef<boolean>(true);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);

      const data: SettingsResponse = await apiCall(Routes.SETTINGS, HttpMethod.GET);
      if (!isMountedRef.current) return;

      const safeData = data || defaultSettings;
      setSettings(safeData);
      prevSettingsRef.current = safeData;
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('Fetch settings error:', err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const flushUpdates = useCallback(async () => {
    const updates = { ...pendingUpdatesRef.current };
    if (Object.keys(updates).length === 0) return;

    pendingUpdatesRef.current = {};
    try {
      await apiCall(Routes.SETTINGS, HttpMethod.PUT, updates);
      enqueueSnackbar('Settings updated successfully.', { variant: 'success' });
      prevSettingsRef.current = {
        ...prevSettingsRef.current,
        ...updates
      };
    } catch (err) {
      console.error('Update settings error:', err);
      setSettings(prevSettingsRef.current);
    }
  }, []);

  const updateSetting = useCallback(
    <K extends keyof SettingsBody>(key: K, value: SettingsBody[K]) => {
      setSettings((prev) => {
        const updated = { ...prev, [key]: value };
        return updated;
      });

      pendingUpdatesRef.current[key] = value;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        flushUpdates();
      }, 500);
    },
    [flushUpdates]
  );

  useEffect(() => {
    fetchSettings();

    return () => {
      isMountedRef.current = false;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [fetchSettings]);

  return {
    settings,
    loading,
    updateSetting,
    refetch: fetchSettings
  };
}
